import { OOFProvidersEnum } from './../../common/enums/oofproviders.enum';
import { SubmitCartService } from './../../common/services/order/cart/submit-cart.service';
import { SingleLineConfiguration } from './../../common/models/line-configurations.model';
import { Component, computed, effect, inject, OnDestroy } from "@angular/core";
import { BaseComponent } from "../../common/components/base.component";
import { CreditErrorType } from "../../common/enums/credit-error-type.enum";
import { OrderStateServiceWrapper } from "../../common/store/state/order/order.state.wrapper.service";
import { OrderService } from "../../common/services/order/order.service";
import { orderStepMetadata } from "./order-step-metadata";
import { DomSanitizer } from "@angular/platform-browser";
import { FormBuilder } from "@angular/forms";
import { PreOrderDetails, WirelessCreditDecisionResponse, WirelessPDPlusChoice, WirelessPDPlusResponse } from "../../common/models/OrderDetails";
import { OrderMenuService } from "../../common/services/order/order-menu.service";
import { LineConfigurationState } from "../../common/models/line-configurations.model";
import { ViLineData } from "../../common/models/sigfan.model";
import { OrderApiResponseStateService } from "../../common/store/state/order/orderv2/order-api-response/order-api-response-state.service";
import { OrderStateServiceV2 } from "../../common/store/state/order/orderv2/order-state.service";
import { WirelessFlowType } from "../../common/models/order/workflow/wireless/wireless-flow.model";
import { OrderCustomizationsStateService } from "../../common/store/state/order/orderv2/workflows/order-customization/order-customization.state.service";
import { InternetFlowStateService } from "../../common/store/state/order/orderv2/workflows/internet/internet-flow-state.service";
import { WirelessFlowStateService } from "../../common/store/state/order/orderv2/workflows/wireless/wireless-flow-state.service";
import { CreditCheckStateService } from "../../common/store/state/order/orderv2/workflows/credit-check/credit-check-state.service";
import { WirelessIdAuthenticationStateService } from "../../common/store/state/order/orderv2/workflows/wireless/wireless-id-authentication.state.service";
import { Flow, FlowTypes, isVoiceProductType, OrderFlowType, PRODUCT_STATUS, PRODUCT_TYPES, ProductStatusType, ServiceType, StepStatusType } from "../../common/models/order/order.model";
import { OrderQualificationResponseService } from "../../common/store/state/order/orderv2/order-api-response/order-oq-response.service";
import { VideoFlowStateService } from "../../common/store/state/order/orderv2/workflows/video/video-flow-state.service";
import { VoiceFlowStateService } from '../../common/store/state/order/orderv2/workflows/voice/voice-flow-state.service';
import { OrderApiCacheService } from "../../common/store/state/order/orderv2/order-api-response/order-api-cache.service";
import { VideoProductResponseService } from '../../common/store/state/order/orderv2/order-api-response/video-pd-response.service';
import { InternetProductResponseService } from '../../common/store/state/order/orderv2/order-api-response/internet-pd-response.service';
import { isClosingOfferGroup } from '../../common/constants/app-constants';
import { Customization } from '../../common/models/order/api-models/order-pd-response.model';
import { tap, finalize, switchMap, Observable, of, map, EMPTY } from 'rxjs';
import { OrderOQResponse, FDE } from '../../common/models/order/api-models/order-oq-response.model';
import { DeviceShippingOption } from '../../common/models/ShippingOption';
import { VideoPackageTypeEnum } from '../../common/enums/video-package-type.enum';
import { UserStateService } from '../../common/store/state/user/user.state.service';
import { RetailersEnum } from '../../common/enums/retailers.enum';
import { ProductResponse as ServiceQualificationProductResponse } from '../../common/models/order/api-models/order-sq-response.model';
import { ProductCreditDecision } from '../../common/models/order/api-models/order-cc-response.model';
import { OrderFlowNavigationService } from './flows/order-flow-navigation.service';
import { OrderStepperService } from './flows/order-stepper.service';
import { FlowStepData } from './flows/flow.types';
import { OrderPayloadHelperService } from '../../common/services/order/order-payload-helper.service';
import { normalizeRequirementFlags } from '../../common/util/order-requirements-util';
import { OrderSetupStateService } from '../../common/store/state/order/orderv2/workflows/order-setup/order-setup-state.service';
import { isTrueFlag } from '../../common/util/value-util';
import { FdeNotice, FdeNoticeService } from '../../common/services/fde-notice.service';

@Component({
    template: '',
    standalone: false
})
export abstract class OrderBaseComponent extends BaseComponent implements OnDestroy {
    protected readonly baseFb = inject(FormBuilder);
    protected orderMenuNotificationService: OrderMenuService = inject(OrderMenuService);
    protected orderCustomizationStateService = inject(OrderCustomizationsStateService);
    protected internetFlowStateService = inject(InternetFlowStateService);
    protected videoFlowStateService = inject(VideoFlowStateService);
    protected voiceFlowStateService = inject(VoiceFlowStateService);
    protected wirelessFlowStateService = inject(WirelessFlowStateService);
    protected creditCheckStateService = inject(CreditCheckStateService);
    protected wirelessIdAuthenticationStateService = inject(WirelessIdAuthenticationStateService);
    protected oqResponseService = inject(OrderQualificationResponseService);
    protected videoPdResponseService = inject(VideoProductResponseService);
    readonly #internetPdResponseService = inject(InternetProductResponseService);
    protected submitCartService = inject(SubmitCartService);
    protected userStateService = inject(UserStateService);
    protected orderFlowNavigationService = inject(OrderFlowNavigationService);
    protected orderStepperService = inject(OrderStepperService);
    protected isQualificationPending = false;
    private qualificationPayloadHelper = inject(OrderPayloadHelperService);
    protected fdeNoticeService = inject(FdeNoticeService);
    creditRiskDialogMessage = '';
    riskClassOptions = ['HIGH', 'MEDIUM', 'LOW', 'UNKNOWN']
    creditRiskDialogVisible = false;
    creditRiskDialogHeader = '';
    orderStateService = inject(OrderStateServiceWrapper)
    orderApiCacheService = inject(OrderApiCacheService)
    orderStateServiceV2 = inject(OrderStateServiceV2)
    #orderSetupState = inject(OrderSetupStateService)
    orderQualificationResponseService = inject(OrderQualificationResponseService)
    orderApiResponseStateService = inject(OrderApiResponseStateService)
    #sanitizer = inject(DomSanitizer)
    orderService = inject(OrderService)
    currentFlow = this.orderStateServiceV2.currentFlowType;
    availableServices = this.orderStateService.getAvailableServices
    internetProductDetails = this.orderStateService.getInternetProductDetails
    videoProductDetails = this.orderStateService.getVideoProductDetails
    wirelessProductDetails = this.orderStateService.getWirelessProductDetails
    wirelessProductDetailsPlus = this.orderStateService.getWirelessProductDetailsPlus
    creditCheckDetails = this.orderStateService.getCreditCheckDetails
    orderQualificationDetails = this.oqResponseService.orderOQResponse;
    internetUpdateOfferDetails = this.orderStateService.getInternetUpdateOfferData
    videoUpdateOfferDetails = this.orderStateService.getVideoUpdateOfferData
    order = this.orderStateServiceV2.order;
    preOrderDetails = this.orderApiResponseStateService.preOrderData

    constructor() {
        super()
        effect(() => {
            const orderStateUpdated = this.order();
            if (orderStateUpdated) {
                this.onOrderDataChanged(orderStateUpdated);
            }
            if (this.getOrderNumber()) {
                this.titleService.update({ title: 'Orders', subTitle: this.getOrderNumber() });
            }
        });
    }

    protected normalizeCode(code: unknown): string {
        return String(code ?? '').trim();
    }

    protected isSuccessResponseCode(code: unknown): boolean {
        // valid: 0, 00, 000, 0000, etc.
        return /^0+$/.test(this.normalizeCode(code));
    }

    protected isErrorResponseCode(code: unknown): boolean {
        return !this.isSuccessResponseCode(code);
    }

    productResponse = computed(() => {
        return this.availableServices()?.Response?.ProductResponse
    })

    sqComponents = computed(() => {
        return this.availableServices()?.Components?.Component
    })

    isVideoAvailable = computed(() => {
        return this.isDtvAvailable() || this.isAtvAvailable()
    })

    isDtvAvailable = computed(() => {
        return this.productResponse()?.some((item) => item.Type === VideoPackageTypeEnum.DTV && item.Code !== '2005')
    })

    isAtvAvailable = computed(() => {
        return this.productResponse()?.some((item) => item.Type === VideoPackageTypeEnum.ATV && item.Code !== '2005')
    })

    allowOptionalEmail = computed(() => {
        return this.preOrderDetails()?.AllowOptionalEmail === true
    })

    orderType = this.#orderSetupState.orderType;

    videoPackageType = computed(() => {
        return this.order()?.videoFlow?.packageType; // DTV (satellite) or ATV (stream)
    })

    emailVerifiedByCustomer = computed(() => {
        return this.order()?.serviceInfoCollection?.customerInformation?.isEmailVerifiedByCustomer ?? false;
    });

    isCommercial = this.#orderSetupState.isCommercial;

    isSMB = this.#orderSetupState.isSMB;

    isResidential = computed(() => {
        return this.orderType() !== 'smb'
    })

    isCommercialVideo = computed(() => {
        return this.isCommercial() && this.videoPackageSelected()
    })

    isResidentialVideo = computed(() => {
        return this.isResidential() && this.videoPackageSelected()
    })

    isWirelessAvailable = computed(() => {
        return this.productResponse()?.some((item) => item.Type === PRODUCT_TYPES.WIRELESS && item.Code !== '2005')
    })

    getWirelessNewCustomer = computed(() => {
        return this.availableServices()?.Components.Component?.find((item) => item.Code === this.getNewCustomerCode())
    })

    getWirelessExistingCustomer = computed(() => {
        return this.availableServices()?.Components.Component?.find((item) => item.Code === this.getExistingCustomerCode())
    })

    getExistingCustomerCode = computed(() => { return 'WRLS-COMP-EXG' })

    getNewCustomerCode = computed(() => { return 'WRLS-COMP-NEW' })

    isWirelessNewCustomerAvailable = computed(() => {
        return !!this.getWirelessNewCustomer()
    })

    isWirelessExistingCustomerAvailable = computed(() => {
        return !!this.getWirelessExistingCustomer()
    })

    isInternetAvailable = computed(() => {
        return this.productResponse()?.some((item) => item.Type === PRODUCT_TYPES.INTERNET)
    })

    isInternetPackagesAvailable = computed(() => {
        return this.availableServices()?.Components?.Component?.filter((item) => item.Category.toLowerCase() === PRODUCT_TYPES.INTERNET.toLowerCase())?.length > 0
    })

    isVoiceAvailable = computed(() => {
        return this.productResponse()?.some((item) => isVoiceProductType(item.Type) && item.Code !== '2005') && this.featureFlagService.isVoiceFlowEnabled()
    })

    voiceData = computed(() => {
        return this.availableServices()?.Components?.Component?.filter((item) => item.Category?.toLowerCase() === PRODUCT_TYPES.VOICE.toLowerCase())
            .sort((a, b) => (Number(a.SequenceNumber) || 0) - (Number(b.SequenceNumber) || 0))
    })

    voiceProductDetails = computed(() => {
        return this.orderApiResponseStateService.voiceProductDetailsData()
    })

    selectedVoicePackageFromState: any = computed(() => {
        return this.voiceData()?.find(item => item.Code === this.voicePackageSelected()?.ComponentCode)
    })

    voicePackageSelected = computed(() => {
        return this.voiceProductDetails()?.Components?.Component?.find(item => isVoiceProductType(item.ProductType))
    })

    isDtvCommercialCEPSmallBarPackageSelected = computed(() => {
        return this.dtvCommerciaSatellitePackageSelected()?.ComponentName?.includes('Small Bar Offer');
    })

    isVoiceCompleted(): boolean {
        const currentStatuses = this.orderStateServiceV2.serviceStatuses();
        return currentStatuses.some(s => s.key === PRODUCT_TYPES.VOICE && s.status === PRODUCT_STATUS.COMPLETED)
    }

    // Voice cannot be selected until the Internet flow has been completed
    isVoiceUnlocked(): boolean {
        return this.isInternetCompleted()
    }

    isProtectionAvailable = computed(() => {
        return this.productResponse()?.some((item) => item.Type === 'Protection' && item.Code === '0000')
    })

    internetData = computed(() => {
        return this.availableServices()?.Components.Component.filter((item) => item.Category.toLowerCase() === PRODUCT_TYPES.INTERNET.toLowerCase())
    })

    internetProductBlocked = computed(() => {
        return this.productResponse()?.find((item) => item.Type === PRODUCT_TYPES.INTERNET && item.Code === '2009' && !this.internetData()?.length)
    })

    internetProductConditional = computed(() => {
        return this.productResponse()?.find((item) => item.Type === PRODUCT_TYPES.INTERNET && item.Code === '2009' && this.internetData()?.length && this.order()?.internetFlow?.status !== StepStatusType.Completed)
    })

    internetProductDisclosure = computed(() => {
        return this.#sanitizer.bypassSecurityTrustHtml(this.productResponse()?.find((item) => item.Type === PRODUCT_TYPES.INTERNET && item.Code === '2009')?.Description)
    })

    wirelessProduct2009 = computed(() => {
        return this.productResponse()?.find((item) => item.Type === PRODUCT_TYPES.WIRELESS && item.Code === '2009')
    })

    // notifyOnly 2009 (e.g. existing wireless customer) is shown to the agent but does not block wireless
    wirelessProductBlocked = computed(() => {
        const product = this.wirelessProduct2009()
        return product && !isTrueFlag(product.NotifyOnly) ? product : undefined
    })

    wirelessProductNotifyOnly = computed(() => {
        const product = this.wirelessProduct2009()
        return product && isTrueFlag(product.NotifyOnly) ? product : undefined
    })

    commercialPackageDisclosure = computed(() => {
        const product = this.availableServices()?.Response?.ProductResponse
            ?.find(t => t.Type === this.videoPackageType());

        const replacedDisclosure = product?.Disclosure
            ? product.Disclosure.replace(/866-347-1279/g, '1-888-388-4249')
            : undefined;

        //replace hyphens with non-breaking hyphens to prevent line breaks in the phone number, if disclosure exists
        const formattedDisclosure = (product?.DisclosureHtml ?? replacedDisclosure)?.replace(/-/g, '&#8209;');

        return formattedDisclosure;
    });

    videoData = computed(() => {
        return this.availableServices()?.Components?.Component?.filter((item) => item.Category.toLowerCase() === PRODUCT_TYPES.VIDEO.toLowerCase()).sort((a, b) => (Number(a.SequenceNumber) || 0) - (Number(b.SequenceNumber) || 0))
    })

    videoProductBlocked = computed(() => {
        return this.productResponse()?.find((item) => (item.Type === VideoPackageTypeEnum.ATV || item.Type === VideoPackageTypeEnum.DTV) && item.Code === '2009' && !this.videoData()?.length)
    })

    videoProductConditional = computed(() => {
        return this.productResponse()?.find((item) => (item.Type === VideoPackageTypeEnum.ATV || item.Type === VideoPackageTypeEnum.DTV) && item.Code === '2009' && this.videoData()?.length)
    })

    hasMyFreeResidentialStreamAccount = computed(() => {
        return this.isResidential() &&
            !!this.dtvStreamPackageSelected() &&
            !!this.productResponse()?.some((item) =>
                this.isMyFreeVideoProductResponse(item)
            );
    })

    protected isMyFreeVideoProductResponse(item: ServiceQualificationProductResponse): boolean {
        const productType = this.normalizeCode(item?.Type).toUpperCase();
        const subCode = this.normalizeCode(item?.SubCode).toLowerCase();
        const description = this.normalizeCode(item?.Description).toLowerCase();
        const isMyFreeSubCode = subCode === 'frontporch' ||
            subCode === 'frontporch-reconnect';

        return productType === VideoPackageTypeEnum.ATV &&
            this.normalizeCode(item.Code) === '2009' &&
            isMyFreeSubCode &&
            description.includes('myfree');
    }

    aTVWscProductDisclosure = computed(() => {
        return this.productResponse()?.find((item) => item.Type === VideoPackageTypeEnum.ATV && item.Code === '2009')?.Description
    })

    dTVWscProductDisclosure = computed(() => {
        return this.productResponse()?.find((item) => item.Type === VideoPackageTypeEnum.DTV && item.Code === '2009')?.Description
    })

    orderCustomer = computed(() => {
        const customer = this.orderStateService.getOrderCustomer();
        return customer;
    })

    get customerInformationCreditCheckFormValue() {
        return this.orderStateServiceV2.order()?.creditCheck?.customerInformationCreditCheck || null;
    }

    getPreOrder = computed(() => {
        return this.orderStateService.getPreOrder() as PreOrderDetails;
    })

    getDealerCode = computed(() => {
        return this.orderStateServiceV2.order()?.orderSetup?.salesInformation?.selectedDealerCode;
    });

    getStartOrder = this.orderStateService.getStartOrder;

    get getSalesInformationForm() {
        return this.orderStateServiceV2.order()?.orderSetup?.salesInformation || null;
    }

    get getRetailerName() {
        return this.getSalesInformationForm?.retailer || '';
    }

    isOnHandRetailer(): boolean {
        const components = this.wirelessProductDetails()?.Components?.Component ?? [];
        const wirelessComponents = components.filter(component =>
            component?.ProductType?.toUpperCase() === 'WIRELESS');

        return wirelessComponents.some(component =>
            this.hasOnHandWirelessDevices(component?.Customizations?.Customization));
    }

    private hasOnHandWirelessDevices(customizations: Customization[] | null | undefined): boolean {
        for (const customization of customizations ?? []) {
            if (customization?.SubGroupName === 'WirelessDevices' && customization.IsOnHand === true) {
                return true;
            }

            for (const choice of customization?.Choices?.Choice ?? []) {
                if (this.hasOnHandWirelessDevices(choice?.Customizations?.Customization)) {
                    return true;
                }
            }
        }

        return false;
    }

    getVIAccount = computed(() => {
        return this.orderStateService.getVIAccountDetails() as any;
    })

    getVIWirelessAccount = computed(() => {
        return this.orderStateService.getVIWirelessAccountDetails() as any;
    })

    // Shared display rules so bundle homepage and product summary always show the same product icons
    canDisplayInternet = computed(() => {
        return (this.isInternetPackagesAvailable() || !!this.internetProductBlocked()) && !this.isCommercial()
    })

    canDisplayWireless = computed(() => {
        return !this.isCommercial()
    })

    canDisplayVoice = computed(() => {
        return !this.isCommercial()
    })

    // AT&T SMB never offers Video, even when SQ returns a DTV/ATV row
    canDisplayVideo = computed(() => {
        return !this.isSMB()
    })

    internetDialog = false
    internetProductConditionAccepted = false
    internetDisclosure = false
    wirelessDialog = false
    wirelessNoticeAcknowledged = false

    preWirelessSelection() {
        if (this.wirelessProductBlocked() || (this.wirelessProductNotifyOnly() && !this.wirelessNoticeAcknowledged)) {
            this.wirelessDialog = true
            return false
        }
        return true
    }

    acknowledgeWirelessNotice() {
        this.wirelessDialog = false
        this.wirelessNoticeAcknowledged = true
    }

    preInternetSelection() {
        if (this.internetProductBlocked() || (this.internetProductConditional() && !this.internetProductConditionAccepted)) {
            this.internetDialog = true
            return false
        }
        return true
    }

    updateServiceStatusOnPlanSelection(serviceType: ServiceType) {
        this.recordServiceSelectionOrder(serviceType)
        this.updateServiceStatusInState(serviceType, ProductStatusType.STARTED_BUT_NOT_FINISHED)
    }

    updateServiceStatusOnPlanUnselection(serviceType: ServiceType) {
        this.updateServiceStatusInState(serviceType, ProductStatusType.AVAILABLE_NOT_STARTED)
        this.clearServiceSelectionOrder(serviceType)
    }

    recordServiceSelectionOrder(serviceType: ServiceType): boolean {
        const currentStatuses = this.orderStateServiceV2.serviceStatuses()
        const existingStatus = currentStatuses.find(status => status.key === serviceType)
        if (!existingStatus || existingStatus.selectionOrder !== undefined) return false

        const selectionOrder = currentStatuses.reduce(
            (maxOrder, status) => Math.max(maxOrder, status.selectionOrder ?? 0),
            0
        ) + 1

        this.orderStateServiceV2.updateOrder({
            serviceStatuses: currentStatuses.map(status =>
                status.key === serviceType ? { ...status, selectionOrder } : status
            )
        })
        return true
    }

    protected clearServiceSelectionOrder(serviceType: ServiceType) {
        const currentStatuses = this.orderStateServiceV2.serviceStatuses()
        if (!currentStatuses.some(status => status.key === serviceType && status.selectionOrder !== undefined)) return

        this.orderStateServiceV2.updateOrder({
            serviceStatuses: currentStatuses.map(status =>
                status.key === serviceType ? { ...status, selectionOrder: undefined } : status
            )
        })
    }

    // Get the next service to continue based on priority
    getNextServiceToContinue(): string | null {
        const currentStatuses = this.orderStateServiceV2.serviceStatuses();

        // Find services that are started but not finished, ordered by priority
        const incompleteServices = currentStatuses
            .filter(s => s.status === PRODUCT_STATUS.STARTED_BUT_NOT_FINISHED && s.priority)
            .sort((a, b) => a.priority - b.priority)

        return incompleteServices.length > 0 ? incompleteServices[0].key : null
    }

    // Check if any service is completed
    hasCompletedServices(): boolean {
        const currentStatuses = this.orderStateServiceV2.serviceStatuses();
        return currentStatuses.some(s => s.status === PRODUCT_STATUS.COMPLETED);
    }

    isInternetCompleted(): boolean {
        const currentStatuses = this.orderStateServiceV2.serviceStatuses();
        return currentStatuses.some(s => s.key === PRODUCT_TYPES.INTERNET && s.status === PRODUCT_STATUS.COMPLETED)
    }

    isVideoCompleted(): boolean {
        const currentStatuses = this.orderStateServiceV2.serviceStatuses();
        return currentStatuses.some(s => s.key === PRODUCT_TYPES.VIDEO && s.status === PRODUCT_STATUS.COMPLETED)
    }

    isWirelessCompleted(): boolean {
        const currentStatuses = this.orderStateServiceV2.serviceStatuses();
        return currentStatuses.some(s => s.key === PRODUCT_TYPES.WIRELESS && s.status === PRODUCT_STATUS.COMPLETED);
    }

    isProtectionCompleted(): boolean {
        const currentStatuses = this.orderStateServiceV2.serviceStatuses();
        return currentStatuses.some(s => s.key === PRODUCT_TYPES.PROTECTION && s.status === PRODUCT_STATUS.COMPLETED)
    }

    // Existing wireless product on the order whose account authentication was never completed.
    hasUnauthenticatedExistingWireless(): boolean {
        const wirelessSetup = this.orderStateServiceV2.order()?.wirelessFlow?.wirelessSetup;
        return wirelessSetup?.selectedCustomerCode === WirelessFlowType.EXISTING
            && !wirelessSetup?.accountAuthenticationComplete;
    }

    // Check if there are incomplete flows
    hasIncompleteFlows(): boolean {
        const currentStatuses = this.orderStateServiceV2.serviceStatuses();
        return currentStatuses.some(s => s.status === PRODUCT_STATUS.STARTED_BUT_NOT_FINISHED);
    }

    updateServiceStatusOnFlowCompletion(serviceType: ServiceType) {
        this.updateServiceStatusInState(serviceType, ProductStatusType.COMPLETED)
    }

    isSecurityAnswerRequired = computed(() => {
        return this.currentOrderRequirementsFromPD()?.SecurityAnswer || false;
    })

    isSecurityPinRequired = computed(() => {
        return this.currentOrderRequirementsFromPD()?.SecurityPin || false;
    })

    isSecureDataRequiredFromPD = computed(() => {
        return this.currentOrderRequirementsFromPD()?.SecureDataRequired || false;
    })

    isSecureDataRequiredCreditFromPD = computed(() => {
        return this.currentOrderRequirementsFromPD()?.SecureDataRequiredCredit || false;
    })

    isSecureDataRequiredTermsFromOQ = computed(() => {
        return this.currentOrderRequirementsFromOQ()?.SecureDataRequiredTerms || false;
    })

    isSecureDataRequiredChecklistFromOQ = computed(() => {
        return this.currentOrderRequirementsFromOQ()?.SecureDataRequiredChecklist || false;
    })

    isSecureDataRequiredPaymentFromOQ = computed(() => {
        return this.currentOrderRequirementsFromOQ()?.SecureDataRequiredPayment || false;
    })

    isCommercialVideoPortal = computed(() => {
        const user = this.userStateService.getUser();
        return this.isCommercial() && this.videoPackageSelected() && !user?.SamlAuthenticated;
    })

    isSecureDataRequiredPaymentInAnyFlow = computed(() => {
        const i = this.orderQualificationDetails()?.OrderRequirements?.Internet
        const v = this.orderQualificationDetails()?.OrderRequirements?.Video
        const w = this.orderQualificationDetails()?.OrderRequirements?.Wireless
        return (i?.SecureDataRequiredPayment || v?.SecureDataRequiredPayment || w?.SecureDataRequiredPayment) || false;
    })

    notPresent = computed(() => {
        return this.orderStateServiceV2.order()?.orderSetup?.orderOptions?.interactionType === 'not-present'
    })

    present = computed(() => {
        return this.orderStateServiceV2.order()?.orderSetup?.orderOptions?.interactionType === 'present'
    })

    allowShippingAddressChange(): boolean {
        return this.currentOrderRequirementsFromUO()?.AllowAltShipping
            ?? this.currentOrderRequirementsFromCC()?.AllowAltShipping
            ?? false;
    }

    creditSetup = computed(() => {
        return this.currentOrderRequirementsFromPD()?.CreditSetup;
    })

    allowBillingAddressChange = computed(() => {
        const value = this.creditSetup()?.AllowBillingAddressChange;
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') return value.toLowerCase() !== 'false';
        return false;
    });

    creditCheckRequired = computed(() => {
        const value = this.creditSetup()?.CreditCheckRequired;
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') return value.toLowerCase() !== 'false';
        return false;
    });

    showBillingAddressAccordion = computed(() => {
        return this.allowBillingAddressChange() && this.isResidentialVideo();
    });

    isResidentialSatelliteBillingAddressEnabled = computed(() => {
        return this.currentFlow() === FlowTypes.VIDEO
            && this.isResidential()
            && !!this.dtvSatellitePackageSelected()
            && this.allowBillingAddressChange();
    });

    isSuccessCode(code: string): boolean {
        return code === "0000";
    }

    anyLineHasStorePickupOrOnHand(): boolean {
        const lineConfigurations = this.orderStateServiceV2.order()?.wirelessFlow?.lineConfigurations as LineConfigurationState;

        if (!lineConfigurations) {
            return false;
        }

        // Check if any line has shipToStore or on-hand device selected
        return Object.values(lineConfigurations).some(
            lineData => lineData?.device?.shippingOption === DeviceShippingOption.StorePickUp || lineData?.device?.deviceType === 'ONHAND'
        );
    }

    anyLineHasNewDeviceType(): boolean {
        const lineConfigurations = this.orderStateServiceV2.order()?.wirelessFlow?.lineConfigurations as LineConfigurationState;

        if (!lineConfigurations) {
            return false;
        }

        // Check if any line has shipped device selected
        return Object.values(lineConfigurations).some(
            lineData => lineData?.device?.deviceType === 'NEW'
        );
    }

    checkIfLinesHaveOnlyStorePickup(): boolean {
        const lineConfigurations = this.orderStateServiceV2.order()?.wirelessFlow?.lineConfigurations as LineConfigurationState;

        if (!lineConfigurations) {
            return false;
        }

        //check all lines and see if they have only store pickup
        return Object.values(lineConfigurations)
            .filter((line: SingleLineConfiguration) => line?.device)
            .every(
                lineData => {
                    return lineData?.device?.shippingOption
                        ? lineData.device.shippingOption === DeviceShippingOption.StorePickUp
                        : lineData?.device?.transactionType === 'existing';
                });
    }

    // In-store pickup lines need no shipping address.
    allLinesInStoreOnly(): boolean {
        const lineConfigurations = this.orderStateServiceV2.order()?.wirelessFlow?.lineConfigurations as LineConfigurationState;
        if (!lineConfigurations) {
            return false;
        }
        const lines = Object.values(lineConfigurations).filter((line: SingleLineConfiguration) => line?.device);
        return lines.length > 0 && lines.every(
            (line: SingleLineConfiguration) => line?.device?.shippingOption === DeviceShippingOption.InStock
        );
    }

    updateServiceStatusInState(serviceKey: ServiceType, status: ProductStatusType) {
        const currentStatuses = this.orderStateServiceV2.serviceStatuses();
        const existingStatus = currentStatuses.find(s => s.key === serviceKey)

        // Only update if status actually changed
        if (existingStatus?.status === status) {
            return
        }

        // // Don't allow downgrading from COMPLETED status unless explicitly setting to COMPLETED
        // if (existingStatus?.status === PRODUCT_STATUS.COMPLETED && status !== PRODUCT_STATUS.COMPLETED) {
        //     return
        // }

        switch (serviceKey) {
            case PRODUCT_TYPES.INTERNET:
                this.internetFlowStateService.updateStatus(status);
                break;
            case PRODUCT_TYPES.VIDEO:
                this.videoFlowStateService.updateStatus(status);
                break;
            case PRODUCT_TYPES.WIRELESS:
                this.wirelessFlowStateService.updateStatus(status);
                break;
            case PRODUCT_TYPES.PROTECTION:
                // Protection doesn't have a flow, so just update status
                break;
            case PRODUCT_TYPES.VOICE:
                this.voiceFlowStateService.updateStatus(status);
                break;
            default:
                break;
        }

        // Update the service status in the order state
        this.orderStateServiceV2.updateServiceStatus(serviceKey, status);
    }

    currentProductDetails = computed(() => {
        const flow = this.currentFlow()
        switch (flow) {
            case FlowTypes.INTERNET:
                return this.internetProductDetails()
            case FlowTypes.VIDEO:
                return this.videoProductDetails()
            case FlowTypes.WIRELESS:
                return this.wirelessProductDetails()
            case FlowTypes.VOICE:
                return this.voiceProductDetails()
            default:
                return null
        }
    })

    productDetailsByFlow = (flow: OrderFlowType) => {
        switch (flow) {
            case FlowTypes.INTERNET:
                return this.internetProductDetails()
            case FlowTypes.VIDEO:
                return this.videoProductDetails()
            case FlowTypes.WIRELESS:
                return this.wirelessProductDetails()
            case FlowTypes.VOICE:
                return this.voiceProductDetails()
            default:
                return null
        }
    }

    currentOrderRequirementsFromPD = computed(() => {
        const flow = this.currentFlow()
        const orderReq = this.currentProductDetails()?.OrderRequirements;
        switch (flow) {
            case FlowTypes.INTERNET:
                return orderReq?.Internet
            case FlowTypes.VIDEO:
                return this.videoUpdateOfferDetails()?.OrderRequirements?.Video ?? orderReq?.Video
            case FlowTypes.WIRELESS:
                return orderReq?.Wireless
            case FlowTypes.VOICE:
                // PD has no dedicated Voice order requirements; using Internet's setup requirements
                return orderReq?.Voice ?? orderReq?.Internet
            default:
                return null
        }
    })

    currentOrderRequirementsFromUO = computed(() => {
        const flow = this.currentFlow()
        switch (flow) {
            case FlowTypes.INTERNET:
                return this.internetUpdateOfferDetails()?.OrderRequirements?.Internet
            case FlowTypes.VIDEO:
                return this.videoUpdateOfferDetails()?.OrderRequirements?.Video
            default:
                return null
        }
    })

    // CC+ (preferred over CC once called) has sent these flags as "true"/"false" strings.
    currentOrderRequirementsFromCC = computed(() => {
        const flow = this.currentFlow()
        const orderReq = this.creditCheckDetails()?.OrderRequirements;
        switch (flow) {
            case FlowTypes.INTERNET:
                return normalizeRequirementFlags(orderReq?.Internet)
            case FlowTypes.VIDEO:
                return normalizeRequirementFlags(orderReq?.Video)
            case FlowTypes.WIRELESS:
                return normalizeRequirementFlags(orderReq?.Wireless)
            default:
                return null
        }
    })

    currentOrderRequirementsFromOQ = computed(() => {
        const flow = this.currentFlow()
        const orderReq = this.orderQualificationDetails()?.OrderRequirements;
        switch (flow) {
            case FlowTypes.INTERNET:
                return orderReq?.Internet
            case FlowTypes.VIDEO:
                return orderReq?.Video
            case FlowTypes.WIRELESS:
                return orderReq?.Wireless
            default:
                return null
        }
    })

    getCreditOrderRequirementsByFlow(flow: OrderFlowType) {
        const creditOrderRequirements = this.creditCheckDetails()?.OrderRequirements;
        switch (flow) {
            case FlowTypes.INTERNET:
                return normalizeRequirementFlags(creditOrderRequirements?.Internet)
            case FlowTypes.VIDEO:
                return normalizeRequirementFlags(creditOrderRequirements?.Video)
            case FlowTypes.WIRELESS:
                return normalizeRequirementFlags(creditOrderRequirements?.Wireless)
            default:
                return null
        }
    }

    orderRequirements = computed(() => {
        return this.currentProductDetails()?.OrderRequirements
    })

    currentLegalCustomizations = computed(() => {
        const productDetails = this.currentProductDetails()
        const flow = this.currentFlow()

        if (!productDetails?.LegalCustomizations?.Customization || !flow) {
            return null
        }

        const subGroupName = `${flow}CreditCheckVerbiage`
        return productDetails.LegalCustomizations.Customization.find(
            (item) => item.SubGroupName === subGroupName
        ) || null
    })

    currentOneTrustCustomizations = computed(() => {
        const productDetails = this.currentProductDetails()
        const flow = this.currentFlow()

        if (!productDetails?.LegalCustomizations?.Customization || !flow) {
            return null
        }

        const subGroupName = `DTVOneTrustVerbiage`
        return productDetails.LegalCustomizations.Customization.find(
            (item) => item.SubGroupName === subGroupName
        ) || null
    })

    currentProvider = computed(() => {
        const flow = this.currentFlow()
        if (!flow) return ''

        if (flow === FlowTypes.INTERNET) {
            return this.orderStateServiceV2.order()?.internetFlow?.provider
        }

        if (flow === FlowTypes.WIRELESS) {
            return this.orderStateServiceV2.order()?.wirelessFlow?.wirelessSetup?.selectedCustomerCode;
        }

        // Fallback to service availability
        const services = this.availableServices()
        const component = services?.Components?.Component?.find(
            c => c.Category?.toLowerCase() === flow.toLowerCase()
        )

        return component?.Provider || ''
    })

    selectedPackageFromState(): any {
        switch (this.currentFlow()) {
            case FlowTypes.INTERNET:
                return this.selectedInternetPackageFromState()
            case FlowTypes.VIDEO:
                return this.selectedVideoPackageFromState()
            case FlowTypes.WIRELESS:
                return this.selectedWirelessPackageFromState()
            case FlowTypes.VOICE:
                return this.selectedVoicePackageFromState()
            default:
                return null
        }
    }

    selectedInternetPackageFromState: any = computed(() => {
        return this.availableServices()?.Components?.Component?.filter((item) => item.Category.toLowerCase() === PRODUCT_TYPES.INTERNET.toLowerCase())
            .find(item => item.Code === this.internetPackageSelected()?.ComponentCode)
    })

    selectedVideoPackageFromState: any = computed(() => {
        return this.availableServices()?.Components?.Component?.filter((item) => item.Category.toLowerCase() === PRODUCT_TYPES.VIDEO.toLowerCase())
            .find(item => item.Code === this.videoPackageSelected()?.ComponentCode)
    })

    selectedHardwarePackageFromState: any = computed(() => {
        return this.order()?.videoFlow?.streamhardwareSelection?.hardwareOptionName
    })

    noOfHardwarePackagesSelectedFromState: any = computed(() => {
        return this.order()?.videoFlow?.streamhardwareSelection?.numberofHardware || 0
    })

    hasSelectedVideoDevices = computed(() => {
        const videoFlow = this.order()?.videoFlow
        return Number(videoFlow?.streamhardwareSelection?.numberofHardware) > 0
            || Number(videoFlow?.satelliteHardwareSelection?.tvQty?.quantity) > 0
    })


    selectedWirelessPackageFromState: any = computed(() => {
        return this.availableServices()?.Components?.Component?.filter((item) => item.Category.toLowerCase() === PRODUCT_TYPES.WIRELESS.toLowerCase())
            .find(item => item.Code === this.wirelessProductDetails()?.Components?.Component[0].ComponentCode)
    })

    anyPackageSelectedFromState = computed(() => {
        return this.selectedInternetPackageFromState() || this.selectedVideoPackageFromState() || this.selectedWirelessPackageFromState() || this.selectedVoicePackageFromState()
    })

    anyPackageSelected = computed(() => {
        return this.videoPackageSelected() || this.internetPackageSelected() || this.wirelessPackageSelected() || this.voicePackageSelected()
    })

    internetPackageSelected = computed(() => {
        return this.internetProductDetails()?.Components.Component.find(item => item.ProductType.toLowerCase() === PRODUCT_TYPES.INTERNET.toLowerCase() || item.ProductType.toLowerCase() === 'OOF'.toLowerCase())
    })

    videoPackageSelected = computed(() => {
        return this.dtvStreamPackageSelected() ? this.dtvStreamPackageSelected() : (this.dtvSatellitePackageSelected() ? this.dtvSatellitePackageSelected() : undefined)
    })


    dtvStreamPackageSelected = computed(() => {
        return this.videoProductDetails()?.Components?.Component.find(item => item.ProductType.toLowerCase() === VideoPackageTypeEnum.ATV.toLowerCase())
    })

    dtvSatellitePackageSelected = computed(() => {
        return this.videoProductDetails()?.Components?.Component.find(item => item.ProductType.toLowerCase() === VideoPackageTypeEnum.DTV.toLowerCase())
    })

    dtvCommerciaStreamPackageSelected = computed(() => {
        return this.isCommercial() && this.dtvStreamPackageSelected()
    })

    dtvCommerciaSatellitePackageSelected = computed(() => {
        return this.isCommercial() && this.dtvSatellitePackageSelected()
    })

    genrePackageSelected = computed(() => {
        return this.dtvStreamPackageSelected()?.ComponentCode?.includes('GENRE')
    })

    closingOffersAvailable = computed(() => this.videoPdResponseService.getCustomizations()?.some(cust =>
        cust.customization.DetailedGroupName === 'ClosingOffers' ||
        cust.customization.DetailedGroupName === 'ClosingOptionalOffers' ||
        cust.customization.DetailedGroupName === 'ClosingAdditionalOffers'
    ) ?? false)

    internetClosingOffersAvailable = computed(() => this.#internetPdResponseService.getCustomizations()?.some(cust =>
        isClosingOfferGroup(cust.customization.DetailedGroupName)
        && cust.customization.FlowVisible?.toLowerCase() !== 'false'
    ) ?? false)

    wirelessPackageSelected = computed(() => {
        return this.wirelessProductDetails()?.Components?.Component.find(item => item.ProductType.toLowerCase() === PRODUCT_TYPES.WIRELESS.toLowerCase())
    })

    wirelessPdPlus = computed(() => {
        return this.wirelessProductDetailsPlus();
    })

    getWirelessPdplusLineData(lineNumber: number) {
        return this.orderStateService.getWirelessPdplusLineData(lineNumber);
    }

    getOrderCustomerInfo() {
        return this.orderStateServiceV2.order()?.serviceInfoCollection?.customerInformation;
    }

    getPreOrderDetails() {
        return this.orderStateService.getPreOrder();
    }


    getOrderCustomerId() {
        return this.orderStateService.getPreOrder()?.CustomerId;
    }

    getOrderAddressInfo() {
        return this.orderStateServiceV2.order()?.serviceInfoCollection?.serviceAvailability;
    }

    getOrderNumber(): string {
        return this.orderStateService.getOrderNumber();
    }

    getCreditCheckDetails() {
        const creditCheckDetails = this.orderStateService.getCreditCheckDetails();
        return creditCheckDetails;
    }

    getProductCreditDecisions(): ProductCreditDecision {
        const creditCheckDetails = this.getCreditCheckDetails();
        return creditCheckDetails?.ProductCreditDecisions?.ProductCreditDecision;
    }

    getSelectedProductCode = computed(() => {
        return this.getProductCreditDecisions()?.ComponentCode || '';
    });

    getCreditDecision() {
        return this.getCreditDecisionOnType(this.currentFlow());
    }

    getCustomizations() {
        return null;
    }

    getCreditDecisionOnType(type: OrderFlowType | Flow): any | WirelessCreditDecisionResponse | null {
        return this.orderStateService.getCreditDecisionOnType(type);
    }

    isOOFProvider(): boolean {
        return this.orderStateServiceV2.isOOFProvider();
    }

    isOOFFrontierProvider(): boolean {
        return this.orderStateServiceV2.isOOFFrontierProvider();
    }

    isOOFWindstreamProvider(): boolean {
        return this.orderStateServiceV2.isOOFWindstreamProvider();
    }

    isCheckboxOptionalOfferProvider(): boolean {
        return this.orderStateServiceV2.isCheckboxOptionalOfferProvider();
    }

    isOOFEarthlinkProvider(): boolean {
        return this.orderStateServiceV2.isOOFEarthlinkProvider();
    }

    isOOFConsolidatedProvider(): boolean {
        return this.orderStateServiceV2.isOOFConsolidatedProvider();
    }

    isSubmitScheduleDateDeferred(): boolean {
        return this.orderStateServiceV2.isSubmitScheduleDateDeferred();
    }

    getCreditClass() {
        return this.getCreditDecision()?.CreditClass;
    }

    numberOfWirelessLinesApproved(): number {
        return this.capWirelessLinesToRequested(this.getCreditDecision()?.NumberOfLinesEligible);
    }

    /**
     * Credit can return more eligible lines than were requested (AT&T SMB returns the
     * bureau's approvedSubscriberLines uncapped), so never approve more than the
     * NumberOfLinesRequested sent with credit check.
     */
    capWirelessLinesToRequested(numberOfLinesEligible: string | number | null | undefined): number {
        const eligible = Number(numberOfLinesEligible) || 0;
        const requested = Number(this.creditCheckStateService.state()?.customerPreferences?.requestedLines) || 0;
        return requested > 0 ? Math.min(eligible, requested) : eligible;
    }

    numberOfWirelessLinesInProgress(): number {
        return this.orderStateServiceV2.order()?.wirelessFlow?.numberOfWirelessLinesInProgress || 0;
    }

    getExistingLines(): ViLineData[] {
        const lines = this.getVIWirelessAccount()?.Lines?.Line
        return lines || [];
    }

    hasAttCreditRisk() {
        const attHighRiskCredit = this.getPreOrderDetails()?.AttHighRiskCredit
        const attMediumRiskCredit = this.getPreOrderDetails()?.AttMediumRiskCredit;
        const creditDecision = this.getCreditDecision();

        let result = false;
        if ((creditDecision?.CreditClass === CreditErrorType.HIGH || creditDecision?.CreditClass === CreditErrorType.UNKNOWN)) {
            if (attHighRiskCredit === 0) {
                result = true;
            }
        }
        else if (creditDecision?.CreditClass === CreditErrorType.MEDIUM) {
            if (attMediumRiskCredit === 0) {
                result = true;
            }
        }

        return result;
    }

    creditDecisionNextRoute(res?: any, previousAddressHandled = false): string {
        const creditDecision = this.getCreditDecision();
        const creditClass = creditDecision?.CreditClass;
        const decisionCode = creditDecision?.DecisionCode;
        const collectPreviousAddress = creditDecision?.CollectPreviousAddress as string;
        // Check if a SIGFAN (FAN) number was returned from viAccount response
        const viWirelessAccount = this.getVIWirelessAccount();
        const existingFan = viWirelessAccount?.Fan;
        const hasSigfanNumber = !!existingFan && existingFan.trim() !== '';
        const shouldSkipSigfan = this.canSkipSigFan(hasSigfanNumber);

        if (creditDecision) {
            if (collectPreviousAddress?.toLowerCase() === 'true' && !previousAddressHandled) {
                return this.orderRoutes.PREVIOUS_ADDRESS_COLLECTION;
            }
            if (creditClass === 'OTP') {
                return this.orderRoutes.IDENTITY_VERIFICATION;
            }
            if (creditClass === 'NCVC') {
                return this.orderRoutes.NCVC_QUESTIONNAIRE;
            }

            if (creditClass === 'REVIEW') {
                return this.orderRoutes.ACCOUNT_REVIEW;
            }

            if (decisionCode === 'AR') {
                return this.orderRoutes.ACCOUNT_REVIEW;
            }

            if (
                creditClass === 'OB' &&
                (creditDecision.CcPlusAction === 'DebtPayment' || creditDecision.CcPlusAction === 'DebtPaymentATT')
            ) {
                return this.orderRoutes.OUTSTANDING_BALANCE;
            }

            if (this.isSMB() && this.isNewWirelessCustomer() && this.currentFlow() === FlowTypes.WIRELESS) {
                return this.afterWirelessIdAuthentication(this.orderRoutes.MOBILE_BUSINESS_AGREEMENT);
            }

            if (shouldSkipSigfan && this.currentFlow() === FlowTypes.WIRELESS) {
                return this.afterWirelessIdAuthentication(this.orderRoutes.LINE_CONFIGURATION);
            }

            //CA Code is only for wireless creditcheck successful case.
            if (decisionCode === 'CA') {
                // SIGFAN is a residential-only step; SMB flows go straight to line configuration.
                return this.afterWirelessIdAuthentication(
                    this.isSMB() ? this.orderRoutes.LINE_CONFIGURATION : this.orderRoutes.SIGFAN
                );
            }

            if (this.riskClassOptions.includes(creditClass?.toUpperCase())) {
                return this.determineNextRouteInFlow()
            }
        }

        // For OOF provider
        const oofSuccess = res?.ResponseHeader?.ProductResponse?.find(
            (response: any) => response.Code === '0000' && response.Type.toLowerCase() === this.currentFlow().toLowerCase()
        );
        if (this.isOOFProvider() && oofSuccess) {
            return this.determineNextRouteInFlow()
        }

        return null;
    }

    // Wireless ID Authentication sits between the credit steps and the rest of the wireless flow.
    private afterWirelessIdAuthentication(route: string): string {
        return this.currentFlow() === FlowTypes.WIRELESS && !this.wirelessIdAuthenticationSatisfied()
            ? this.orderRoutes.WIRELESS_ID_AUTHENTICATION
            : route;
    }

    wirelessIdAuthenticationSatisfied(): boolean {
        return this.getCreditDecision()?.AuthIdRequired !== 'true'
            || !!this.wirelessIdAuthenticationStateService.state()?.complete;
    }

    canSkipSigFan(hasSigfanNumber: boolean): boolean {
        // New wireless customers must complete SigFan before it can be skipped.
        if (this.isNewWirelessCustomer() && !this.isSigfanCompleted()) {
            return false;
        }

        return (
            !this.getVIWirelessAccount() ||
            hasSigfanNumber ||
            this.isSigfanCompleted()
        );
    }

    determineNextRouteInFlow(): string {
        if (this.orderStateService.advancedBillPaymentRequired() && this.currentFlow() === FlowTypes.INTERNET) {
            return this.orderRoutes.ADVANCED_BILL_PAYMENT;
        }

        if (this.orderStateService.creditResultsRequired() && this.currentFlow() === FlowTypes.VIDEO) {
            return this.orderRoutes.CREDIT_RESULTS;
        }

        if (this.genrePackageSelected()) {
            return this.hasMyFreeResidentialStreamAccount()
                ? this.orderRoutes.ALTERNATE_SHIPPING
                : this.orderRoutes.EMAIL_VALIDATION;
        }

        const flowRouteMap = {
            [FlowTypes.INTERNET]: this.orderRoutes.ADDITIONAL_INTERNET_OFFERS,
            [FlowTypes.VIDEO]: this.orderRoutes.ADDITIONAL_VIDEO_OFFERS,
            [FlowTypes.WIRELESS]: this.orderRoutes.ADDITIONAL_WIRELESS_OFFERS
        };

        const flow = this.currentFlow();
        return flowRouteMap[flow];
    }

    formatPhoneNumber(phoneNumber: string): string {
        const input = phoneNumber.replace(/\D/g, '').substring(0, 10) // Remove non-digits and limit to 10 digits
        const areaCode = input.substring(0, 3)
        const middle = input.substring(3, 6)
        const last = input.substring(6)

        if (input.length > 6) {
            return `${areaCode}-${middle}-${last}`
        } else if (input.length > 3) {
            return `${areaCode}-${middle}`
        } else if (input.length > 0) {
            return `${areaCode}`
        } else {
            return ''
        }
    }

    getFlowBackRoute(): string {
        const flow = this.currentFlow()
        const routeMap = {
            [FlowTypes.INTERNET]: this.orderRoutes.INTERNET_PACKAGE_SELECTION,
            [FlowTypes.VIDEO]: this.orderRoutes.VIDEO_PACKAGE_SELECTION,
            [FlowTypes.WIRELESS]: (this.isNewWirelessCustomer() && this.orderType() === 'smbnew')
                ? this.orderRoutes.BUSINESS_VERIFICATION
                : this.orderRoutes.WIRELESS_SETUP
        }
        if (this.genrePackageSelected()) {
            return this.closingOffersAvailable() ? this.orderRoutes.CLOSING_OFFERS : this.orderRoutes.ADDITIONAL_CHANNEL_OFFERS
        } else if (this.dtvStreamPackageSelected()) {
            return this.orderRoutes.STREAM_HARDWARE_SELECTION
        } else if (this.dtvSatellitePackageSelected()) {
            return this.orderRoutes.SATELLITE_HARDWARE_SELECTION
        } else {
            return routeMap[flow]
        }
    }

    isNewWirelessCustomer(): boolean {
        return this.orderStateServiceV2.order()?.wirelessFlow?.wirelessSetup?.selectedCustomerCode
            === this.getNewCustomerCode();
    }

    isExistingWirelessCustomer(): boolean {
        return this.orderStateServiceV2.order()?.wirelessFlow?.wirelessSetup?.selectedCustomerCode
            === this.getNewCustomerCode();
    }


    get isCharter(): boolean {
        return this.getProvider().toLowerCase() === OOFProvidersEnum.SPECTRUM
    }

    isCostco(): boolean {
        return this.getRetailerName?.toLowerCase() === RetailersEnum.COSTCO
    }

    getProvider(): string {
        const flow = this.currentFlow();
        if (!flow) return '';

        const order = this.order();

        switch (flow) {
            case FlowTypes.INTERNET:
                return order.internetFlow?.provider?.toLowerCase() === OOFProvidersEnum.CHARTER ? 'Spectrum' : order.internetFlow?.provider || '';

            case FlowTypes.WIRELESS:
                return order.wirelessFlow?.wirelessSetup?.selectedCustomerCode || '';

            case FlowTypes.VIDEO:
                return order.videoFlow?.provider || '';

            default: {
                // TypeScript thinks 'flow' is 'never' here because all Enum members
                // were handled above. We cast to 'string' to allow the comparison.
                const flowStr = (flow as string).toLowerCase();

                const services = this.availableServices();
                const component = services?.Components?.Component?.find(
                    c => c.Category?.toLowerCase() === flowStr
                );

                return component?.Provider || '';
            }
        }
    }

    removeProduct() {
        const flow = this.currentFlow()
        this.orderStateService.setCreditCheck(null)
        // Clear product details based on current flow
        switch (flow) {
            case FlowTypes.INTERNET:
                this.orderStateService.setInternetProductDetails(null)
                break
            case FlowTypes.VIDEO:
                this.orderStateService.setVideoProductDetails(null)
                break
            case FlowTypes.WIRELESS:
                this.orderStateService.setWirelessProductDetails(null)
                this.orderStateService.setWirelessProductDetailsPlus(null)
                break
        }

        this.navigate([this.orderRoutes.BUNDLE_HOMEPAGE])
    }

    showCreditRiskDialog(header: string, message: string) {
        this.creditRiskDialogMessage = message;
        this.creditRiskDialogVisible = true;
        this.creditRiskDialogHeader = header;
    }

    hideCreditRiskDialog() {
        this.creditRiskDialogMessage = '';
        this.creditRiskDialogVisible = false;
        this.creditRiskDialogHeader = '';
        this.removeProduct();
    }

    setOrderCurrentRoute(route: string[] | string) {
        const routeStr = Array.isArray(route) ? route.join('/') : route;
        this.orderStateService.setCurrentRoute(routeStr);
    }

    anyCompletedFlows = computed(() => {
        const o = this.order();
        const currentKey = o.flowType?.toUpperCase();
        return o.serviceStatuses.filter(
            s => s.key !== currentKey && s.status === 'COMPLETED'
        );
    });

    isBundleOrder = computed(() => this.anyCompletedFlows().length > 0);

    navigate(route: any[] | any, fromCreditCheck = false) {
        if (fromCreditCheck) {
            this.creditCheckStateService.setCreditSubmitted(true);
            this.qualificationPayloadHelper.clearQualifiedSelection(this.currentFlow());
        }
        this.orderStepperService.recordNavigation(this, this.router.url, this.routeToString(route));
        this.setOrderCurrentRoute(route);
        // The navigation spinner is driven globally by NavigationSpinnerService, which starts it only once the
        // router's guards have passed.
        this.router.navigate(Array.isArray(route) ? route : [route], {
            state: { skipUrlValidation: true }
        });
    }

    /** Stepper jump to a page the agent already reached; unlike navigate() it never completes the page left. */
    goToStep(route: string) {
        this.setOrderCurrentRoute(route);
        this.router.navigate([route], { state: { skipUrlValidation: true } });
    }

    private routeToString(route: string[] | string): string {
        return Array.isArray(route) ? route.join('/') : route;
    }

    canReuseQualifiedSelections(): boolean {
        return this.isResidential() && !this.isSMB() && !!this.orderQualificationDetails() && !!this.creditCheckDetails()
            && this.qualificationPayloadHelper.matchesQualifiedSelection(this.currentFlow());
    }

    nextStep(data?: FlowStepData, fromCreditCheck = false) {
        const route = this.orderFlowNavigationService.getNextRoute(this, data);
        if (route) {
            this.navigate([route], fromCreditCheck);
        }
    }

    // Pages opt into qualification after completing their own validation and state updates.
    protected runOqAndContinueToNextStep(data?: FlowStepData): void {
        if (this.isQualificationPending) return;
        if (this.skipsGenreCreditCheckPage(data)) {
            this.runOptionalCreditCheck(() => this.navigate([this.determineNextRouteInFlow()]));
            return;
        }
        if (!this.isResidential() || this.currentFlow() === FlowTypes.VOICE
            || this.peekNextRoute(data) !== this.orderRoutes.PRODUCT_SUMMARY) {
            this.nextStep(data);
            return;
        }

        this.processOrderQualification_OQ(() => this.nextStep(data), { qualificationOnly: true });
    }

    // Genre reaches Credit Check after the offers pages, so the Stream hardware rule is applied here.
    private skipsGenreCreditCheckPage(data?: FlowStepData): boolean {
        return this.genrePackageSelected() && !this.isCommercial() && !this.creditCheckRequired()
            && this.peekNextRoute(data) === this.orderRoutes.CREDIT_CHECK;
    }

    // Credit check not required: run CC in the background without showing the Credit Check page.
    protected runOptionalCreditCheck(onSuccess: () => void): void {
        this.spinnerService.setLoading(true, 'credit-check-optional');
        this.subscriptions.add(
            this.orderService.getCreditCheck_CC_Optional()
                .pipe(
                    tap((resp) => {
                        const errObj = this.getErrorResponse(resp);
                        if (errObj) {
                            this.notificationService.showError(errObj?.Message || 'Error, please try later.');
                            return;
                        }
                        onSuccess();
                    }),
                    finalize(() => this.spinnerService.setLoading(false, 'credit-check-optional'))
                )
                .subscribe()
        );
    }

    backStep(data?: FlowStepData) {
        const route = this.orderFlowNavigationService.getBackRoute(this, data);
        if (route) {
            this.navigate([route]);
        }
    }

    peekNextRoute(data?: FlowStepData): string | null {
        return this.orderFlowNavigationService.getNextRoute(this, data);
    }

    peekBackRoute(data?: FlowStepData): string | null {
        return this.orderFlowNavigationService.getBackRoute(this, data);
    }

    /**
     * Utility: Find route data for a given route string using static step metadata
     * Matches by last segment of the path
     */
    getRouteDataForPath(path: string): any {
        // Normalize path: get last segment
        const lastSegment = path.split('/').filter(Boolean).pop();
        const meta = orderStepMetadata.find(m => {
            const metaSegment = m.path.split('/').filter(Boolean).pop();
            return metaSegment === lastSegment;
        });
        return meta ? meta.data : null;
    }

    refreshOrderMenuModel() {
        this.orderMenuNotificationService.notifyDataChanged();
    }

    getLineConfigurationState(): LineConfigurationState {
        return this.orderStateService.getLineConfigurationState();
    }

    // To get all line numbers that have configurations
    getConfiguredLineNumbers(): number[] {
        return this.orderStateService.getConfiguredLineNumbers();
    }

    get isWirelessFlow(): boolean {
        return this.currentFlow() === FlowTypes.WIRELESS;
    }

    findChoiceInPdplusBySubGroupNameOrChoiceCode(lineData: WirelessPDPlusResponse, subGroupName?: string, choiceCode?: string): WirelessPDPlusChoice | null {
        return this.orderStateService.findChoiceInPdplusBySubGroupNameOrChoiceCode(lineData, subGroupName, choiceCode);
    }

    isSigfanCompleted(): boolean {
        return this.order()?.wirelessFlow?.sigfan?.isSigfanCompleted || false;
    }

    //This method will be invoked whenever there is a change in order data either by setting state or getting data from api
    protected onOrderDataChanged(forms: any): void {
        // Override in derived classes if needed
        return forms;
    }

    extractWirelessResponseStatus(resp: any): boolean {
        const successResp = resp.ResponseHeader.ProductResponse.find((product) => {
            return product.Type === 'WIRELESS' && product.Code === '0000'
        })
        return !!successResp;
    }

    extractWirelessConditionalResponse(resp: any): any {
        const conditionalResp = resp.ResponseHeader.ProductResponse.find((product) => {
            return product.Type === 'WIRELESS' && product.Code === '0001'
        })
        return conditionalResp;
    }

    extractOOFResponse(resp: any): any {
        const oofResp = resp.ResponseHeader.ProductResponse.find((product) => {
            return product.Type === 'OOF' && product.Code === '0000'
        })
        return oofResp;
    }


    retrieveWirelessResponse(resp: any) {
        return resp.ResponseHeader?.ProductResponse?.find(
            (response: any) => response.Type.toLowerCase() === 'wireless'
        )
    }

    protected getFDEFromResponse(response: OrderOQResponse): FDE | null {
        return response.Components?.Component?.[0]?.FDE ?? null;
    }

    protected buildFdeNotice(fde: FDE): FdeNotice {
        const wirelessDecision = this.getCreditDecisionOnType(FlowTypes.WIRELESS);
        return {
            header: fde.MessageHeader || 'Action Required',
            messageHtml: fde.MessageHtml || (fde.Message ?? 'Action is required before the order can proceed.').replace(/\\n|\n/g, '<br>'),
            creditReferenceNumber: this.getCreditDecision()?.CreditReferenceNumber || wirelessDecision?.CreditReferenceNumber || '',
            ban: wirelessDecision?.BillingAccountNumber || this.getVIWirelessAccount()?.AccountNumber || ''
        };
    }

    protected isActionRequiredWithoutCancellation(fde: FDE): boolean {
        const actionRequired = fde.ActionRequired?.toLowerCase() === 'true';
        const orderNotCancelled = fde.OrderCancelled?.toLowerCase() === 'false';
        return actionRequired && orderNotCancelled;
    }

    protected isOrderCancelled(fde: FDE): boolean {
        const actionRequired = fde.ActionRequired?.toLowerCase() === 'true';
        const orderCancelled = fde.OrderCancelled?.toLowerCase() === 'true';
        return actionRequired && orderCancelled;
    }

    /**
     * Runs after a successful OQ, before onSuccess/navigation.
     * Commercial: submits the cart only.
     * All other flows: submits the cart, then calls Update Offers (UO)
     * and stores the UO response.
     * @returns Observable that completes once the post-OQ calls finish.
     */
    protected afterOrderQualification(): Observable<any> {
        if (this.isCommercial()) {
            return this.submitCartService.submitCart();
        }
        return this.submitCartService.submitCart().pipe(
            switchMap(() => this.orderService.updateOffers_UO())
        );
    }

    // Qualification-only calls validate the response without running checkout actions.
    protected processOrderQualification_OQ(
        onSuccess: (response: OrderOQResponse) => void,
        { qualificationOnly = false }: { qualificationOnly?: boolean } = {}
    ): void {
        if (this.isQualificationPending) return;
        // Any started product must be finished before checkout OQ (single or bundled).
        if (!qualificationOnly && this.hasIncompleteFlows()) {
            this.notificationService.showError('One or more products are in progress. You must complete them before continuing.');
            return;
        }

        this.isQualificationPending = true;
        this.spinnerService.setLoading(true, 'order-qualification');

        const qualificationSnapshot = !qualificationOnly && this.isResidential() && !this.isSMB()
            ? this.qualificationPayloadHelper.captureQualificationSnapshot() : {};
        if (!qualificationOnly && this.order()?.qualificationSnapshot) {
            this.orderStateServiceV2.updateOrder({ qualificationSnapshot: undefined });
        }

        const hasWirelessLines = (this.order()?.wirelessFlow?.numberOfWirelessLinesInProgress ?? 0) > 0;
        const qualificationRequest = qualificationOnly && hasWirelessLines
            ? this.orderService.refreshWirelessProductDetailsPlus().pipe(
                switchMap(() => this.orderService.getOrderQualificationData_OQ())
            )
            : this.orderService.getOrderQualificationData_OQ();

        this.subscriptions.add(
            qualificationRequest.pipe(
                switchMap(oqResponse => {
                    if (qualificationOnly) {
                        const validation = this.validateOQProductResponse(oqResponse);
                        if (!validation.isValid) {
                            this.notificationService.showError(validation.errorMessage || 'Order qualification failed. Please try again.');
                            return EMPTY;
                        }
                        // FDE action/cancel stays with the checkout OQ on Order Options, which shows the provider
                        // instructions; an FDE hold persists until verification clears, so stopping here loops (11635).
                        this.orderStateService.setOrderQualification(oqResponse);
                        return of(oqResponse);
                    }
                    this.orderStateService.setOrderQualification(oqResponse);
                    // is this order proceedable?
                    if (!this.isOQProceedable(oqResponse)) {
                        return of(oqResponse); // skip submitCart/UO; onSuccess handles the dialog
                    }
                    return this.afterOrderQualification().pipe(
                        switchMap(response => {
                            if (this.getErrorResponse(response) || Object.keys(qualificationSnapshot).length === 0) {
                                return of(undefined);
                            }
                            this.orderStateServiceV2.updateOrder({ qualificationSnapshot });
                            return this.orderStateServiceV2.saveOrderData();
                        }),
                        map(() => oqResponse)
                    );
                }),
                tap(oqResponse => onSuccess(oqResponse)), // checkout callers also handle non-proceedable responses
                finalize(() => {
                    this.isQualificationPending = false;
                    this.spinnerService.setLoading(false, 'order-qualification');
                })
            ).subscribe()
        );
    }

    // Returns true only if the OQ response is valid and the order can proceed; false otherwise.
    private isOQProceedable(response: OrderOQResponse): boolean {
        if (!this.validateOQProductResponse(response).isValid) return false;
        const fde = this.getFDEFromResponse(response);
        if (fde && (this.isOrderCancelled(fde) || this.isActionRequiredWithoutCancellation(fde))) return false;
        return true;
    }

    protected validateOQProductResponse(response: OrderOQResponse): { isValid: boolean; errorMessage?: string } {
        const customMessage = "Order qualification failed. Please Try Again.";

        if (!response) {
            return { isValid: false, errorMessage: customMessage };
        }

        const productResponses = response.ResponseHeader?.ProductResponse;
        if (!Array.isArray(productResponses) || productResponses.length === 0) {
            return { isValid: false, errorMessage: customMessage };
        }

        const failingResponse = productResponses.find(pr => !pr || pr.Code !== '0000');
        if (failingResponse) {
            return { isValid: false, errorMessage: failingResponse?.Message ?? customMessage };
        }

        return { isValid: true };
    }

    hasTermsAndConditions(response: OrderOQResponse): boolean {
        return response?.OrderTerms != null &&
            (response.OrderTerms.Term?.length > 0 || response.Components?.Component?.[0]?.Terms?.Term?.length > 0);
    }

    isDtvCommercialSatelliteInstallationChecklistEnabled(): boolean {
        return this.featureFlagService.isEnabled('enableDtvCommercialSatelliteInstallationChecklist')
            && this.isCommercial()
            && !!this.dtvSatellitePackageSelected();
    }

    getErrorResponse(resp, type?: string): any {
        const products = resp?.ResponseHeader?.ProductResponse;
        if (!Array.isArray(products)) return null;

        const flow = String(type ?? this.currentFlow() ?? '').toLowerCase().trim();
        const errors = products.filter(p => String(p?.Code ?? '').trim() !== '0000');

        // prefer an error matching the current flow's Type, else the first error of any type
        return errors.find(p => String(p?.Type ?? '').toLowerCase().trim() === flow && flow)
            || errors[0]
            || null;
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.titleService.update(undefined);
    }
}
